import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import apiManager from '../../../../api/api-manager';
import {
  ApiErrorCodes,
  parseApiResponseError,
} from '../../../../api/api-error';
import {
  DtaAccountError,
  DtaAccountRequest,
  DtaAccountResponse,
  DtaAccountSearchCriterion,
  DtaAccountSearchRequest,
  DtaAccountSearchResponse,
} from '../../../../api/generated';
import { AsyncThunkConfig } from '../../../async-thunk-config';
import { AdminConsoleState } from '../admin-console-types';
import { FiduciarieSearchFilters, FiduciarieState } from './fiduciarie-types';

export const DTA_SEARCH_PAGE_SIZE = 20;

const initialState: FiduciarieState = {
  searchDtaResponse: undefined,
  searchDtaStatus: 'idle',
  createDtaStatus: 'idle',
  deleteDtaStatus: 'idle',
};

const authHeaders = (state: AdminConsoleState) => ({
  headers: { Authorization: `BEARER ${state.user?.token}` },
});

const filterSchemes: Record<keyof FiduciarieSearchFilters, string> = {
  nome: 'NAME',
  cognome: 'SURNAME',
  ragioneSociale: 'COMPANY_NAME',
  codiceClienteRappresentante: 'LEGAL_ENTITY_CODE',
  codiceFiscale: 'CODICE_FISCALE',
  codiceFiscaleFiduciante: 'ACTING_PERSON_CODICE_FISCALE',
  addressBook: 'ADDRESS_BOOK',
  partitaIva: 'PARTITA_IVA',
};

export const buildDtaSearchRequest = (
  filters: FiduciarieSearchFilters = {},
  paginaCorrente = 1,
  elementiPerPagina = DTA_SEARCH_PAGE_SIZE
): DtaAccountSearchRequest => ({
  criteria: (Object.keys(filterSchemes) as (keyof FiduciarieSearchFilters)[])
    .map((key) => ({ scheme: filterSchemes[key], value: filters[key]?.trim() }))
    .filter((criterion): criterion is DtaAccountSearchCriterion => !!criterion.value),
  pagination: { paginaCorrente, elementiPerPagina },
});

const badRequestError = (error: unknown): DtaAccountError => {
  const apiError = parseApiResponseError<DtaAccountError>(error);
  if (apiError.responseErrorCode !== ApiErrorCodes.BAD_REQUEST) {
    return {};
  }
  return {
    campiNonValidi: apiError.payload?.campiNonValidi ?? [],
    codiciErrore: apiError.payload?.codiciErrore ?? [],
  };
};

export const searchDta = createAsyncThunk<
  DtaAccountSearchResponse,
  DtaAccountSearchRequest,
  AsyncThunkConfig<DtaAccountError>
>('searchDta', async (request: DtaAccountSearchRequest, thunkApi) => {
  try {
    const state: AdminConsoleState =
      thunkApi.getState().features.adminConsole.common;
    const response = await apiManager.adminConsoleApi.searchDtaAccounts(
      request,
      authHeaders(state)
    );
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue(badRequestError(error));
  }
});

export const createDta = createAsyncThunk<
  DtaAccountResponse,
  DtaAccountRequest,
  AsyncThunkConfig<DtaAccountError>
>('createDta', async (request: DtaAccountRequest, thunkApi) => {
  try {
    const state: AdminConsoleState =
      thunkApi.getState().features.adminConsole.common;
    const response = await apiManager.adminConsoleApi.createDtaAccount(
      request,
      authHeaders(state)
    );
    return response.data;
  } catch (error) {
    return thunkApi.rejectWithValue(badRequestError(error));
  }
});

export const deleteDta = createAsyncThunk<
  string,
  string,
  AsyncThunkConfig<void>
>('deleteDta', async (username: string, thunkApi) => {
  const state: AdminConsoleState =
    thunkApi.getState().features.adminConsole.common;
  await apiManager.adminConsoleApi.deleteDtaAccount(
    username,
    authHeaders(state)
  );
  return username;
});

const fiduciarieSlice = createSlice({
  name: 'features/adminConsole/fiduciarie',
  initialState,
  reducers: {
    resetSearchDtaState(state) {
      state.searchDtaStatus = 'idle';
      state.searchDtaError = undefined;
      state.searchDtaResponse = undefined;
    },
    resetCreateDtaState(state) {
      state.createDtaStatus = 'idle';
      state.createDtaError = undefined;
      state.createDtaResponse = undefined;
    },
    resetDeleteDtaState(state) {
      state.deleteDtaStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchDta.pending, (state) => {
      state.searchDtaStatus = 'loading';
      state.searchDtaError = undefined;
    });
    builder.addCase(
      searchDta.fulfilled,
      (state, action: PayloadAction<DtaAccountSearchResponse>) => {
        state.searchDtaStatus = 'succeeded';
        state.searchDtaResponse = action.payload;
      }
    );
    builder.addCase(searchDta.rejected, (state, action) => {
      state.searchDtaStatus = 'failed';
      state.searchDtaError = action.payload ?? {};
      state.searchDtaResponse = undefined;
    });
    builder.addCase(createDta.pending, (state) => {
      state.createDtaStatus = 'loading';
      state.createDtaError = undefined;
      state.createDtaResponse = undefined;
    });
    builder.addCase(
      createDta.fulfilled,
      (state, action: PayloadAction<DtaAccountResponse>) => {
        state.createDtaStatus = 'succeeded';
        // Holds the temporary password, which the create page shows to the operator once.
        state.createDtaResponse = action.payload;
      }
    );
    builder.addCase(createDta.rejected, (state, action) => {
      state.createDtaStatus = 'failed';
      state.createDtaError = action.payload ?? {};
    });
    builder.addCase(deleteDta.pending, (state) => {
      state.deleteDtaStatus = 'loading';
    });
    builder.addCase(
      deleteDta.fulfilled,
      (state, action: PayloadAction<string>) => {
        state.deleteDtaStatus = 'succeeded';
        if (state.searchDtaResponse) {
          state.searchDtaResponse.data = state.searchDtaResponse.data.filter(
            (account) => account.username !== action.payload
          );
        }
      }
    );
    builder.addCase(deleteDta.rejected, (state) => {
      state.deleteDtaStatus = 'failed';
    });
  },
});

export const fiduciarieSliceActions = fiduciarieSlice.actions;

export default fiduciarieSlice.reducer;
